import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CheckCircle2, XCircle, Clock, FileText, AlertTriangle, FileEdit, Upload } from "lucide-react";
import { toast } from "sonner";
import Pagination from "@/components/Pagination";
import DocumentationModal from "@/components/DocumentationModal";
import EvvImportModal from "@/components/EvvImportModal";

const PAGE_SIZE = 50;

const FILTER_TABS = [
  { key: "all",      label: "All" },
  { key: "missing",  label: "Missing docs" },
  { key: "complete", label: "Documented" },
];

export default function Timesheets() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  // Batch 1 (2026-05-22): DSPs land on the "Missing docs" filter by default so
  // assigned documentation tasks are the first thing they see. Backend list
  // remains scoped to `user_id == self` for DSPs via routers/timesheets.py.
  const [filter, setFilter] = useState(user?.role === "dsp" ? "missing" : "all");
  const [docTarget, setDocTarget] = useState(null);
  const [importOpen, setImportOpen] = useState(false);

  const load = (p = page, f = filter) => {
    const params = { page: p, limit: PAGE_SIZE };
    if (f === "missing")  params.documentation = "missing";
    if (f === "complete") params.documentation = "complete";
    return api.get("/timesheets", { params }).then((r) => {
      setRows(r.data.data); setPage(r.data.page); setPages(r.data.pages); setTotal(r.data.total);
    });
  };
  useEffect(() => { load(1, filter); /* eslint-disable-next-line */ }, [filter]);

  const approve = async (id, ok) => {
    try { await api.post("/timesheets/approve", { timesheet_id: id, approve: ok }); toast.success(ok ? "Approved" : "Rejected"); load(page); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  // Retroactive documentation submit. Used by DSPs to add missing notes
  // on their own completed timesheets AND by admins to add/correct docs
  // on any completed timesheet. The endpoint enforces auth + lock rules.
  const submitDocs = async ({ service_provided, service_note }) => {
    if (!docTarget) throw new Error("No timesheet selected");
    const { data } = await api.post(
      `/timesheets/${docTarget.id}/documentation`,
      { service_provided, service_note },
    );
    toast.success(
      data.is_correction
        ? `Documentation corrected${data.by_role === "admin" ? " (admin override)" : ""}`
        : "Documentation added",
    );
    // In-place row refresh — single timesheet's worth of data is updated
    // optimistically, then we re-fetch the page to pull any audit-side
    // fields the server may have added (e.g., documentation_corrected_*).
    setRows((rs) =>
      rs.map((r) =>
        r.id === docTarget.id
          ? { ...r, service_provided, service_note, documentation_status: "complete" }
          : r,
      ),
    );
    load(page);
  };

  const canEditDocs = (t) => {
    if (!t.clock_out) return false;
    if (user.role === "admin") return true;
    if (t.user_id !== user.id) return false;
    if (t.documentation_locked === true) return false;
    if (t.approval_status === "approved") return false;
    return true;
  };

  return (
    <div className="space-y-8">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="accp-label">Workforce</div>
          <h1 className="text-4xl font-semibold text-primary" style={{ fontFamily: "Outfit" }}>
            {user.role === "dsp" ? "Timesheet history" : "Timesheets"}
          </h1>
          <p className="text-muted-foreground mt-1">
            {user.role === "dsp"
              ? "Your completed visits. Add missing service documentation to imported or legacy shifts."
              : "GPS-stamped clock-in/out, documentation status, and approval workflow."}
          </p>
        </div>
        {user.role === "admin" && (
          <button
            className="accp-btn-primary"
            onClick={() => setImportOpen(true)}
            data-testid="open-evv-import-btn"
          >
            <Upload size={14} /> Import EVV
          </button>
        )}
      </div>

      {/* Filter tabs — visible to everyone, but most useful for admins */}
      <div className="flex gap-2 flex-wrap" data-testid="ts-filter-tabs">
        {FILTER_TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setFilter(t.key)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold ring-1 transition-colors ${
              filter === t.key
                ? "bg-primary text-primary-foreground ring-primary"
                : "bg-white text-foreground ring-border hover:bg-accent/40"
            }`}
            data-testid={`ts-filter-${t.key}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="accp-card overflow-hidden">
        <div className="accp-table-scroll">
          <table className="w-full text-sm">
            <thead className="bg-accent/30 text-left">
              <tr>
                <th className="p-4 accp-label">Code</th>
                <th className="p-4 accp-label" data-testid="ts-col-participant">Participant</th>
                <th className="p-4 accp-label">Staff</th>
                <th className="p-4 accp-label">Clock in</th>
                <th className="p-4 accp-label">Clock out</th>
                <th className="p-4 accp-label">Hours</th>
                <th className="p-4 accp-label">Units</th>
                <th className="p-4 accp-label">Documentation</th>
                <th className="p-4 accp-label">Status</th>
                <th className="p-4 accp-label text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((t) => {
                const docStatus = t.documentation_status
                  || (t.clock_out
                    ? (t.service_provided && t.service_note ? "complete" : "missing")
                    : "live");
                const editable = canEditDocs(t);
                return (
                  <tr key={t.id} className="border-t border-border" data-testid={`ts-${t.code}`}>
                    <td className="p-4 font-mono text-xs text-muted-foreground">{t.code}</td>
                    <td className="p-4" data-testid={`ts-participant-${t.code}`}>
                      {t.individual_name ? (
                        <div>
                          <div className="font-semibold text-primary text-sm">{t.individual_name}</div>
                          <div className="text-[11px] text-muted-foreground font-mono">{t.individual_code}</div>
                        </div>
                      ) : t.service_context && t.service_context !== "participant" ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-slate-100 text-slate-700 ring-1 ring-slate-200">
                          {t.service_context}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-[#FFF8E6] text-[#8A5A00] ring-1 ring-[#FFE4A0]" title="No participant linked — resolve via EVV wizard or edit shift">
                          <AlertTriangle size={10} /> Needs link
                        </span>
                      )}
                    </td>
                    <td className="p-4 font-semibold text-primary">{t.staff_name}</td>
                    <td className="p-4 text-muted-foreground">{t.clock_in ? new Date(t.clock_in).toLocaleString() : "—"}</td>
                    <td className="p-4 text-muted-foreground">
                      {t.clock_out
                        ? new Date(t.clock_out).toLocaleString()
                        : <span className="inline-flex items-center gap-1"><Clock size={12} /> live</span>}
                    </td>
                    <td className="p-4">{t.hours ?? "—"}</td>
                    <td className="p-4 text-xs">{t.billable_units ?? "—"}</td>
                    <td className="p-4">
                      {docStatus === "complete" && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold compliance-valid" data-testid={`doc-status-complete-${t.code}`}>
                          <CheckCircle2 size={11} /> Complete
                        </span>
                      )}
                      {docStatus === "missing" && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-[#FFF8E6] text-[#8A5A00] ring-1 ring-[#FFE4A0]" data-testid={`doc-status-missing-${t.code}`}>
                          <AlertTriangle size={11} /> Missing note
                        </span>
                      )}
                      {docStatus === "live" && (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                      {t.evv_source && (
                        <div className="text-[10px] text-muted-foreground mt-0.5 font-mono">via {t.evv_source}</div>
                      )}
                    </td>
                    <td className="p-4">
                      <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${
                        t.approval_status === "approved" ? "compliance-valid"
                          : t.approval_status === "rejected" ? "compliance-expired"
                            : "compliance-pending"
                      }`}>
                        {t.approval_status}
                      </span>
                    </td>
                    <td className="p-4 text-right space-x-1 whitespace-nowrap">
                      {docStatus === "missing" && editable && (
                        <button
                          className="accp-btn-ghost text-xs"
                          onClick={() => setDocTarget(t)}
                          data-testid={`add-note-btn-${t.code}`}
                        >
                          <FileText size={13} /> Add note
                        </button>
                      )}
                      {docStatus === "complete" && editable && user.role === "admin" && (
                        <button
                          className="accp-btn-ghost text-xs"
                          onClick={() => setDocTarget(t)}
                          data-testid={`edit-note-btn-${t.code}`}
                          title="Admin override — preserves audit history"
                        >
                          <FileEdit size={13} /> Edit
                        </button>
                      )}
                      {user.role !== "dsp" && t.approval_status === "pending" && t.clock_out && (
                        <>
                          <button className="accp-btn-ghost text-xs" onClick={() => approve(t.id, true)} data-testid={`approve-btn-${t.code}`}>
                            <CheckCircle2 size={13} /> Approve
                          </button>
                          <button className="accp-btn-ghost text-xs text-destructive" onClick={() => approve(t.id, false)} data-testid={`reject-btn-${t.code}`}>
                            <XCircle size={13} /> Reject
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
              {rows.length === 0 && (
                <tr><td colSpan={10} className="p-8 text-center text-muted-foreground">
                  {filter === "missing" ? "No timesheets missing documentation." : "No timesheets yet."}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
        <Pagination page={page} pages={pages} total={total} onChange={(p) => load(p)} testId="timesheets-pagination" />
      </div>

      <DocumentationModal
        open={!!docTarget}
        onOpenChange={(v) => { if (!v) setDocTarget(null); }}
        title={docTarget?.service_provided ? "Edit documentation" : "Add service documentation"}
        description={
          docTarget?.evv_source
            ? `Imported visit (${docTarget.evv_source}). Adding documentation does not change the EVV facts.`
            : "Complete the documentation for this visit."
        }
        submitLabel={docTarget?.service_provided ? "Save correction" : "Save documentation"}
        initialService={docTarget?.service_provided || ""}
        initialNote={docTarget?.service_note || ""}
        onSubmit={submitDocs}
      />

      <EvvImportModal
        open={importOpen}
        onOpenChange={setImportOpen}
        onImported={() => load(1, filter)}
      />
    </div>
  );
}
